import { useMutation, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import {
  getEmailSubscriptions,
  getNotificationSettings,
  parseNotificationSettings,
  updateEmailSubscriptions,
  updateNotificationSettings,
} from '@/src/lib/api/endpoints/notifications';
import type {
  AllNotificationPreferences,
  EmailSubscriptions,
  NotificationDeviceGroup,
  NotificationTypeGroup,
} from '@/src/lib/api/endpoints/notifications';
import { useCurrentUser } from './useUserQuery';

export const notificationKeys = {
  settings: ['notifications', 'settings'] as const,
  subscriptions: (email: string) => ['notifications', 'subscriptions', email] as const,
};

export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationKeys.settings,
    queryFn: async () => {
      const raw = await getNotificationSettings();
      return parseNotificationSettings(raw);
    },
    staleTime: 60_000,
  });
}

export function useEmailSubscriptions() {
  const { data: user } = useCurrentUser();
  const email = user?.data?.email ?? '';

  return useQuery({
    queryKey: notificationKeys.subscriptions(email),
    queryFn: () => getEmailSubscriptions(email),
    enabled: !!email,
    staleTime: 60_000,
  });
}

const DEVICE_KEY: Partial<Record<NotificationDeviceGroup, 'email' | 'mobile'>> = {
  EMAIL: 'email',
  NATIVE_MOBILE: 'mobile',
};

const TYPE_KEY: Record<NotificationTypeGroup, keyof AllNotificationPreferences> = {
  REC_SEARCH: 'recSearch',
  SAVED_SEARCH: 'savedSearch',
  PROPERTY_UPDATE: 'propertyUpdate',
};

export function useToggleNotification() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const email = user?.data?.email ?? '';

  return useMutation({
    mutationFn: async ({
      deviceGroup,
      typeGroup,
      optedOut,
    }: {
      deviceGroup: NotificationDeviceGroup;
      typeGroup: NotificationTypeGroup;
      optedOut: boolean;
    }) => {
      await updateNotificationSettings([{ deviceGroup, typeGroup, optedOut }]);
    },
    onMutate: async ({ deviceGroup, typeGroup, optedOut }) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.settings });
      const previous = queryClient.getQueryData<AllNotificationPreferences>(
        notificationKeys.settings,
      );

      if (previous) {
        const deviceKey = DEVICE_KEY[deviceGroup];
        if (!deviceKey) return { previous };

        queryClient.setQueryData<AllNotificationPreferences>(notificationKeys.settings, {
          ...previous,
          [TYPE_KEY[typeGroup]]: {
            ...previous[TYPE_KEY[typeGroup]],
            [deviceKey]: !optedOut,
          },
        });
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationKeys.settings, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.settings });
      if (email) {
        queryClient.invalidateQueries({ queryKey: notificationKeys.subscriptions(email) });
      }
    },
  });
}

export function useToggleAllNotifications() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const email = user?.data?.email ?? '';

  return useMutation({
    mutationFn: async ({ shutOff }: { shutOff: boolean }) => {
      const devices: Array<NotificationDeviceGroup> = ['EMAIL', 'NATIVE_MOBILE'];
      const types: Array<NotificationTypeGroup> = ['REC_SEARCH', 'SAVED_SEARCH', 'PROPERTY_UPDATE'];

      const options = devices.flatMap((deviceGroup) =>
        types.map((typeGroup) => ({ deviceGroup, typeGroup, optedOut: shutOff })),
      );

      const subscriptions: EmailSubscriptions & { email: string } = {
        stopListings: shutOff,
        stopOther: shutOff,
        email,
      };

      await Promise.all([
        updateNotificationSettings(options),
        updateEmailSubscriptions(subscriptions),
      ]);
    },
    onMutate: async ({ shutOff }) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.settings });
      const previousSettings = queryClient.getQueryData<AllNotificationPreferences>(
        notificationKeys.settings,
      );

      const enabled = !shutOff;
      queryClient.setQueryData<AllNotificationPreferences>(notificationKeys.settings, {
        recSearch: { email: enabled, mobile: enabled },
        savedSearch: { email: enabled, mobile: enabled },
        propertyUpdate: { email: enabled, mobile: enabled },
      });

      let previousSubs: EmailSubscriptions | undefined;
      if (email) {
        await queryClient.cancelQueries({ queryKey: notificationKeys.subscriptions(email) });
        previousSubs = queryClient.getQueryData<EmailSubscriptions>(
          notificationKeys.subscriptions(email),
        );
        queryClient.setQueryData<EmailSubscriptions>(notificationKeys.subscriptions(email), {
          stopListings: shutOff,
          stopOther: shutOff,
        });
      }

      return { previousSettings, previousSubs };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(notificationKeys.settings, context.previousSettings);
      }
      if (context?.previousSubs && email) {
        queryClient.setQueryData(notificationKeys.subscriptions(email), context.previousSubs);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.settings });
      if (email) {
        queryClient.invalidateQueries({ queryKey: notificationKeys.subscriptions(email) });
      }
    },
  });
}
